/**
 * 商户路由入口
 * 对应 PHP/Vue2: CRMEB_MER_v4.0/view/mer/src/router/index.js
 * 结构：各 modules 逐文件 import → constantRoutes 内联组装（无 modules/index 聚合层）
 */
import { createWebHistory, createRouter, RouteRecordRaw } from 'vue-router';
/* Layout */
import Layout from '@/layout/index.vue';
import { roterPre } from '@/settings';

/* Router Modules（顺序与 EB view/mer/src/router/index.js 一致） */
import systemFormRouter from './modules/systemForm';
import configRouter from './modules/config';
import settingRouter from './modules/setting';
import groupRouter from './modules/group';
import productRouter from './modules/product';
import marketingRouter from './modules/marketing';
import orderRouter from './modules/order';
import accountsRouter from './modules/accounts';
import communityRouter from './modules/community';
import userRouter from './modules/user';
import stationRouter from './modules/station';
import exportFileRouter from './modules/exportFile';
import deliveryRouter from './modules/delivery';
import deviseRouter from './modules/devise';
import chartsRouter from './modules/charts';
/** TiTa 扩展路由（artist / activity / collaboration 等，非 EB 原厂） */
import expandRouter from './modules/expand';
import memberRouter from './modules/member';

/**
 * Note: 路由配置项
 *
 * hidden: true                     // 当设置 true 的时候该路由不会再侧边栏出现 如401，login等页面
 * alwaysShow: true                 // 当你一个路由下面的 children 声明的路由大于1个时，自动会变成嵌套的模式
 * redirect: noRedirect             // 当设置 noRedirect 的时候该路由在面包屑导航中不可被点击
 * name:'router-name'               // 设定路由的名字，一定要填写不然使用<keep-alive>时会出现各种问题
 * meta : {
    noCache: true                   // 如果设置为true，则不会被 <keep-alive> 缓存(默认 false)
    title: 'title'                  // 设置该路由在侧边栏和面包屑中展示的名字
    icon: 'svg-name'                // 设置该路由的图标，对应路径src/assets/icons/svg
    affix: true                     // 如果设置为true，标签会固定在 tags-view
    breadcrumb: false               // 如果设置为false，则不会在breadcrumb面包屑中显示
    activeMenu: '/system/user'      // 当路由设置了该属性，则会高亮相对应的侧边栏。
  }
 */
declare module 'vue-router' {
  interface RouteMeta {
    hidden?: boolean;
    title?: string;
    icon?: string;
    elSvgIcon?: string;
    permissions?: string[];
    affix?: boolean;
  }
  interface _RouteRecordBase {
    hidden?: boolean;
    /** EB / vue-element-admin：始终显示父级菜单（即使只有一个子路由） */
    alwaysShow?: boolean;
    parentPath?: string;
    permissions?: string[];
  }
  interface _RouteLocationBase {
    title?: string;
  }
}

/**
 * constantRoutes
 * 基础页面，无权限要求，所有角色可访问
 * 对应 EB: export const constantRoutes = [...]
 */
export const constantRoutes: RouteRecordRaw[] = [
  systemFormRouter,
  configRouter,
  settingRouter,
  groupRouter,
  productRouter,
  marketingRouter,
  orderRouter,
  accountsRouter,
  communityRouter,
  userRouter,
  memberRouter,
  stationRouter,
  exportFileRouter,
  deliveryRouter,
  deviseRouter,
  chartsRouter, // P3 示例图（EB modules 有、index 未挂；TITA 挂上便于验收）
  ...expandRouter,
  {
    path: '/redirect',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () => import('@/views/common/redirect/index.vue'),
      },
    ],
  },
  {
    path: roterPre,
    component: Layout,
    redirect: `${roterPre}/dashboard`,
    children: [
      {
        path: `${roterPre}/dashboard`,
        component: () => import('@/views/dashboard/index.vue'),
        name: 'Dashboard',
        meta: { title: '控制台', icon: 'dashboard', affix: true },
      },
      {
        path: `${roterPre}/statistic/order`,
        name: 'statisticOrder',
        meta: {
          title: '订单统计',
          noCache: true,
        },
        component: () => import('@/views/dashboard/order/index.vue'),
      },
      {
        path: `${roterPre}/statistic/product`,
        name: 'statisticProduct',
        meta: {
          title: '商品统计',
          noCache: true,
        },
        component: () => import('@/views/dashboard/product/index.vue'),
      },
    ],
  },
  {
    path: '/',
    component: Layout,
    redirect: `${roterPre}/dashboard`,
    children: [
      {
        path: `${roterPre}/dashboard`,
        component: () => import('@/views/dashboard/index.vue'),
        name: 'Dashboard_',
        meta: { title: '控制台', icon: 'dashboard', affix: true },
      },
    ],
  },
  {
    path: `${roterPre}/login`,
    component: () => import('@/views/login/index.vue'),
    hidden: true,
  },
  {
    path: '/login',
    component: () => import('@/views/login/index.vue'),
    hidden: true,
  },
  {
    path: '/401',
    component: () => import('@/views/common/error/401.vue'),
    hidden: true,
  },
  {
    path: '/siteClosed',
    component: () => import('@/views/common/error/siteClosed.vue'),
    hidden: true,
  },
  {
    path: `${roterPre}/siteClosed`,
    component: () => import('@/views/common/error/siteClosed.vue'),
    hidden: true,
  },
  {
    path: `${roterPre}/404`,
    component: () => import('@/views/common/error/404.vue'),
    hidden: true,
  },
  {
    path: `${roterPre}/setting/icons`,
    component: () => import('@/components/iconFrom/index.vue'),
    name: 'icons',
    hidden: true,
  },
  {
    path: `${roterPre}/setting/uploadPicture`,
    component: () => import('@/components/uploadPictures/index.vue'),
    name: 'uploadPicture',
    hidden: true,
  },
  {
    path: `${roterPre}/setting/storeProduct`,
    component: () => import('@/components/goodList/index.vue'),
    name: 'storeProduct',
    hidden: true,
  },
  {
    path: `${roterPre}/setting/userList`,
    component: () => import('@/components/userList/index.vue'),
    name: 'userList',
    hidden: true,
  },
  {
    path: `${roterPre}/order/export`,
    component: () => import('@/components/exportFile/index.vue'),
    name: 'exportFileList',
    hidden: true,
  },
  {
    path: `${roterPre}/admin/widget/image`,
    name: 'images',
    meta: {
      title: '上传图片',
    },
    component: () => import('@/components/uploadPictures/index.vue'),
    hidden: true,
  },
  {
    path: `${roterPre}/admin/widget/video`,
    name: 'video',
    meta: {
      title: '上传视频',
    },
    component: () => import('@/components/uploadVideo/index.vue'),
    hidden: true,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: `${roterPre}/404`,
    hidden: true,
  },
];

/** EB 权限动态路由（带 permissions 的隐藏页，由 permission store 按需 addRoute） */
export const dynamicRoutes: RouteRecordRaw[] = [];

const router = createRouter({
  history: createWebHistory(),
  routes: [...constantRoutes, ...dynamicRoutes],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

export default router;
